/* storybook-check-ignore */
import { Button } from '@opendoor/bricks/core';
import AccountConnector, { AccountProps } from 'components/shared/AccountConnector';
import { Listing, ListingWithComputedProperties } from 'declarations/exclusives/listing';
import {
  getHasOngoingOrUpcomingOpenHouses,
  getIsSellerOfferExperiment,
  getListingPriceCents,
} from 'helpers/exclusives/listing';
import { useMemo } from 'react';
import { ListingStates } from '../constants/constants';
import useExclusiveAnalytics from '../hooks/useExclusiveAnalytics';

const GOOGLE_FORM_ATTRIBUTES_ENTRY_IDS = {
  username: '118426448',
  phone_number: '192323661',
  email: '457792489',
  address_slug: '373098070',
} as const;

function getRequestATourLink(listing: Listing, account: AccountProps): string {
  const googleFormAttributes: Record<
    keyof typeof GOOGLE_FORM_ATTRIBUTES_ENTRY_IDS,
    string | undefined
  > = {
    username: account.human?.get('full_name'),
    phone_number: account.human?.get('primary_phone'),
    email: account.email || account.human?.get('email'),
    address_slug: listing.slug,
  };
  const queryParams = Object.entries(googleFormAttributes)
    .filter(([_, val]) => !!val)
    .map(
      ([key, val]) =>
        `entry.${
          GOOGLE_FORM_ATTRIBUTES_ENTRY_IDS[key as keyof typeof GOOGLE_FORM_ATTRIBUTES_ENTRY_IDS]
        }=${encodeURIComponent(val as string)}`,
    )
    .join('&');
  return `https://docs.google.com/forms/d/e/1FAIpQLSfYp6Mpuna90YPRFhqaqYd8K-Xg_mzmYA63thqnXybde9lOWA/viewform?usp=pp_url&${queryParams}`;
}

const VISIBLE_EL_STATES = [ListingStates.EL_LISTED, ListingStates.EL_SETUP];

export function shouldShowRequestATourButton(
  listing: ListingWithComputedProperties,
  hasOngoingOrUpcomingOpenHouses = getHasOngoingOrUpcomingOpenHouses(listing),
) {
  const priceCents = getListingPriceCents(listing);
  const hasPrice = !!priceCents && priceCents > 0;

  return (
    getIsSellerOfferExperiment(listing) &&
    (hasOngoingOrUpcomingOpenHouses || hasPrice) &&
    VISIBLE_EL_STATES.includes(listing.el_state)
  );
}

interface Props extends AccountProps {
  listing: ListingWithComputedProperties;
}

function RequestATourButton({ listing, ...account }: Props) {
  const { trackExclusivesEvent } = useExclusiveAnalytics();
  const requestAtourLink = useMemo(() => getRequestATourLink(listing, account), [listing, account]);
  const hasOngoingOrUpcomingOpenHouses = getHasOngoingOrUpcomingOpenHouses(listing);
  if (!shouldShowRequestATourButton(listing)) return null;
  const onClick = () => {
    const featureName = 'exclusives-request-a-tour';
    const label = hasOngoingOrUpcomingOpenHouses ? 'request-another-time' : 'request-a-tour';
    trackExclusivesEvent(featureName, label, listing);
  };
  return (
    <Button
      as="a"
      onClick={onClick}
      aria-label={null}
      target="_blank"
      rel="noopener noreferrer"
      analyticsName="cosmos-exclusives-hero-open-house-request-a-tour-button"
      variant="primary"
      href={requestAtourLink}
    >
      {hasOngoingOrUpcomingOpenHouses ? 'Request another time' : 'Request a tour'}
    </Button>
  );
}

export default AccountConnector(RequestATourButton);
